import { useMemo } from 'react';

import useAccessBadge, { LicenseCoarseLevels } from './useAccessBadge';
import useTeachingCreditState from './useTeachingCreditState';

/**
 * Single source of truth for rendering School plan content across cards, flows,
 * dropdowns, and paywalls. Every surface derives its state from this hook so we
 * can't drift between the home-page card, the in-flow dropdown, and the
 * sidebar counter widget.
 *
 * Trial counts come from `useTeachingCreditState` — the `creditState` response
 * includes `premiumTestPrepPlayed`, `premiumTestPrepLimit`, and the derived
 * `premiumTestPrepRemaining`. Same source the existing
 * `PremiumTestPrepPlaywallProvider` uses, so numbers line up everywhere.
 *
 * Visible-vs-actual: US users get a hidden 4th "forgiveness" attempt (the
 * email-gate modal state captures their email and grants one extra session
 * past the displayed limit). The 4th slot must never surface in any UI count
 * or progress bar — `trialsLeft`/`trialLimit` are clamped to
 * `VISIBLE_TRIAL_LIMIT` here so every consumer reads "X of 3" regardless of
 * the raw backend value. The provider's `pickModalState` keeps reading raw
 * `creditState` directly so the email-gate threshold still triggers.
 */

/**
 * Hard cap on the trial count we ever show the user. Backend may return a
 * larger limit (US forgiveness slot); display always saturates at 3.
 * Re-used as the fallback when `creditState` hasn't loaded yet.
 */
export const VISIBLE_TRIAL_LIMIT = 3;

/**
 * Map raw backend `played` count to the trials-left number we put in the UI.
 *
 * Treat the user as if they have exactly `VISIBLE_TRIAL_LIMIT` trials. The 4th
 * "forgiveness" slot the backend may grant US users is invisible — so the
 * count drops 3 → 2 → 1 → 0 as `played` ticks 0 → 1 → 2 → 3, and stays at 0
 * once the hidden 4th is consumed. Going off `played` (rather than clamping
 * `remaining`) keeps the displayed count honest as soon as the user starts
 * their first trial.
 *
 * Examples (US, backend limit 4):
 *   played=0, remaining=4 → 3   (full)
 *   played=1, remaining=3 → 2
 *   played=2, remaining=2 → 1
 *   played=3, remaining=1 → 0   (email-gate state in the modal)
 *   played=4, remaining=0 → 0   (hard block)
 *
 * Examples (Intl, backend limit 3):
 *   played=0 → 3 · played=1 → 2 · played=2 → 1 · played=3 → 0
 */
export function computeVisibleTrialsLeft(played: number): number {
  return Math.max(0, VISIBLE_TRIAL_LIMIT - played);
}

export type ContentAccessState =
  | { state: 'member' }
  | { state: 'free-slice-and-trial'; trialsLeft: number }
  | { state: 'free-slice-no-trial' }
  | { state: 'no-free-slice-and-trial'; trialsLeft: number }
  | { state: 'no-free-slice-no-trial' };

export type UseSchoolPlanContentAccessArgs = {
  /**
   * Does this piece of content have any free skills/options inside?
   * - Revision & Editing, Grammar Practice: true
   * - Test Prep (today): false
   */
  hasFreeSlice: boolean;
};

export type UseSchoolPlanContentAccessResult = {
  access: ContentAccessState;
  isReady: boolean;
  trialsLeft: number;
  trialLimit: number;
  /** True when the user is enterprise — everything unlocked. */
  isMember: boolean;
  /** True when trialsLeft === 0 AND user is not a member. */
  isTrialExhausted: boolean;
};

export function useSchoolPlanContentAccess({
  hasFreeSlice,
}: UseSchoolPlanContentAccessArgs): UseSchoolPlanContentAccessResult {
  const { accessBadge, isFetched } = useAccessBadge();
  const { creditState, isLoading } = useTeachingCreditState();

  const isReady = isFetched && !isLoading;

  const isMember = accessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise;

  // Source of truth: the teaching-credit-state response's premium test prep
  // fields. Backend currently populates these from the same counter that
  // powers `PremiumTestPrepPlaywallProvider` and the sidebar's teaching-
  // sessions widget — keeps our numbers consistent with both.
  //
  // We derive the visible count from `played` (not from `remaining`) so the
  // UI debits a trial as soon as the user uses one — the hidden forgiveness
  // slot otherwise lets `remaining` linger at the displayed cap and freezes
  // the bar full while the user has actually used their first attempt.
  const played = creditState?.premiumTestPrepPlayed ?? 0;
  const trialLimit = VISIBLE_TRIAL_LIMIT;
  const trialsLeft = computeVisibleTrialsLeft(played);
  const isTrialExhausted = !isMember && trialsLeft <= 0;

  const access = useMemo<ContentAccessState>(() => {
    if (isMember) return { state: 'member' };

    if (hasFreeSlice) {
      if (trialsLeft > 0) return { state: 'free-slice-and-trial', trialsLeft };
      return { state: 'free-slice-no-trial' };
    }

    if (trialsLeft > 0) return { state: 'no-free-slice-and-trial', trialsLeft };
    return { state: 'no-free-slice-no-trial' };
  }, [isMember, hasFreeSlice, trialsLeft]);

  return {
    access,
    isReady,
    trialsLeft,
    trialLimit,
    isMember,
    isTrialExhausted,
  };
}
